import { logger } from '@app/logger';

/**
 * Run a Telegram SDK call that must never be able to take down the app.
 *
 * WHY THIS EXISTS — a production incident, 2026-08-30..09-01. Registration
 * conversion fell from ~31% to ~22% and 231 of 232 crash reports in three
 * hours were a single error: `UnknownEnvError` thrown by
 * `backButton.show.ifAvailable()`. 44% of them were on `/sign-up`, where the
 * back button arms the moment the user leaves step 0 — so the crash landed
 * exactly on "fill in the first screen, press next".
 *
 * The trap is the SDK's API surface. `ifAvailable()` READS like the safe
 * variant, and it does swallow "this method is unsupported" — but it is a
 * GATE, not a try/catch: it checks `isAvailable()` and then calls the raw
 * function with no error handling. And `isAvailable()` is unreliable here,
 * because it includes `isTMA()`, which only checks that launch params can be
 * retrieved — and those are cached (under `tapps/*` in **sessionStorage**;
 * `@telegram-apps/toolkit/dist/index.js:31,34` is the only storage backend in
 * the dependency chain, and nothing in @telegram-apps touches localStorage at
 * all) and written back on every success. So once the app has been launched
 * from Telegram, `isAvailable()` keeps reporting true for the rest of that
 * session on that origin, even after the actual transport (iframe postMessage
 * / TelegramWebviewProxy / external.notify) is gone. `postEvent` then throws.
 *
 * The storage detail is load-bearing, not trivia: sessionStorage is scoped
 * per tab AND per origin, so this is "the transport died during this session"
 * — not a permanently poisoned browser profile. It also means a cross-origin
 * launcher redirect lands on an origin with an EMPTY cache, where launch
 * params have to come from the preserved hash instead.
 *
 * WHY THE CATCH IS BROAD. There is no failure of a host-chrome call for which
 * crashing the app is the right answer — the app renders its own navigation
 * regardless. A narrow `isUnknownEnvError` catch was considered and rejected:
 *   - `TelegramWebviewProxy.postEvent` throws plain `Error`s on Android
 *     WebView teardown ("Java bridge method invocation error", "Java object is
 *     gone" — both already in beforeSend's suppression list), which a narrow
 *     catch would let through, i.e. still a white screen;
 *   - `isUnknownEnvError` is `instanceof` plus a module-private Symbol, so it
 *     silently returns false if the dependency tree ever duplicates
 *     `@telegram-apps/bridge`.
 *
 * Breadth is made safe by separating CATCHING from REPORTING: everything is
 * caught, everything is reported, and `beforeSend` decides what is noise.
 * `UnknownEnvError` and `/^Error invoking postEvent/` are already suppressed
 * there, so the common cases cost no quota — while a genuinely new failure
 * (say an SDK upgrade making `show` throw a TypeError) is NOT suppressed and
 * shows up in Sentry immediately.
 *
 * Wrap ONE SDK call per invocation, never a block of app logic — the broad
 * catch is only defensible because there is nothing else inside it to mask.
 *
 * Note this is intentionally SYNC, unlike the `safe()` helper in
 * `routes/__root.tsx`, which awaits `init()`/`mount()` during boot. A
 * promise-returning helper cannot be used in a `useEffect` cleanup without
 * reintroducing unhandled rejections, so the two shapes stay separate.
 *
 * Returns whether the call actually went through, so callers that have a
 * non-Telegram fallback can use it — e.g. the legal links only call
 * `preventDefault()` when `openLink` succeeded, letting the browser follow the
 * real href otherwise instead of the link silently doing nothing.
 *
 * Note the return value means "did not throw", NOT "the Telegram host acted on
 * it". `.ifAvailable()` no-ops and reports success when the method is gated
 * off, so `true` only implies a real call at raw (non-`ifAvailable`) sites.
 * That is fine for the fallback sites, which all call raw functions.
 *
 * `fn` is constrained to SYNC functions on purpose. Under TypeScript's
 * void-return bivariance a `() => Promise<T>` would otherwise satisfy
 * `() => void`, return `true` instantly, and let the rejection escape
 * unhandled — the exact bug this helper exists to prevent. That matters here
 * because `routes/__root.tsx` has an identically-named, identically-shaped
 * `safe()` which IS async; the two must not be interchangeable by accident.
 *
 * @param label Identifies the call in Sentry via `tags.tmaCall`.
 * @param fn A single, synchronous Telegram SDK invocation.
 * @returns `true` if `fn` completed, `false` if it threw and was swallowed.
 */
export const safeTma = <T>(
  label: string,
  fn: () => T extends Promise<unknown> ? never : T,
): boolean => {
  try {
    fn();
    return true;
  } catch (tmaError) {
    try {
      logger.captureException(tmaError, { tags: { tmaCall: label } });
    } catch {
      // Reporting must not resurrect the crash this exists to prevent.
    }
    return false;
  }
};
